import { PERMISSIONS } from '@ibe/constants';
import { Button, ErrorState, LoadingState, StatusBadge, toneForStatus } from '@ibe/ui';
import { type UseQueryResult } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useGuest, useGuestPii } from '../api/queries.js';
import {
  type GuestAccount,
  type GuestAddress,
  type GuestDetail,
  type GuestPii,
} from '../api/schemas.js';

/**
 * One guest.
 *
 * The directory endpoint returns a masked record. Plaintext PII is fetched
 * through a separate, audited endpoint and only shown to operators who have
 * permission to read it. The page title shows the decrypted first name once
 * the audited PII endpoint returns it.
 */

function GuestDetailContent({
  data,
  pii,
  piiQuery,
}: {
  readonly data: GuestDetail;
  readonly pii: GuestPii | undefined;
  readonly piiQuery: UseQueryResult<GuestPii>;
}): React.JSX.Element {
  return (
    <>
      <DetailGrid>
        <DetailField label="Status">
          <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
        </DetailField>
        <DetailField label="Stays">{data.stays}</DetailField>
        <DetailField label="Created">
          {new Date(data.createdAt).toLocaleDateString('en-AU', {
            timeZone: 'UTC',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </DetailField>
        <DetailField label="Version">{String(data.version)}</DetailField>
      </DetailGrid>

      <section className="mt-section">
        <h2 className="text-body font-semibold">Account</h2>
        <AccountDetail account={data.account} />
      </section>

      <section className="mt-section">
        <h2 className="text-body font-semibold">Addresses</h2>
        <AddressesDetail addresses={data.addresses} />
      </section>

      {piiQuery.isPending ? (
        <LoadingState label="Loading contact details" />
      ) : piiQuery.error !== null ? (
        <ErrorState
          description={errorTextFor(piiQuery.error) ?? ''}
          onRetry={() => {
            void piiQuery.refetch();
          }}
        />
      ) : (
        <section className="mt-section">
          <h2 className="text-body font-semibold">Personal information</h2>
          <p className="text-caption text-muted-foreground">
            This data is encrypted at rest and shown only after an audit log entry.
          </p>
          <DetailGrid>
            <DetailField label="First name">{pii?.firstName}</DetailField>
            <DetailField label="Last name">{pii?.lastName}</DetailField>
            <DetailField label="Email">{pii?.email}</DetailField>
            <DetailField label="Phone">{pii?.phone}</DetailField>
          </DetailGrid>
        </section>
      )}
    </>
  );
}

function AccountDetail({
  account,
}: {
  readonly account: GuestAccount | undefined;
}): React.JSX.Element {
  if (!account?.hasAccount) {
    return <DetailField label="Account status">No account</DetailField>;
  }

  return (
    <DetailGrid>
      <DetailField label="Account status">{account.status}</DetailField>
      <DetailField label="Last login">{account.lastLoginAt}</DetailField>
      <DetailField label="Password changed">{account.passwordChangedAt}</DetailField>
      <DetailField label="Email verified">{account.emailVerifiedAt}</DetailField>
      <DetailField label="Failed login attempts">{String(account.failedLoginAttempts)}</DetailField>
    </DetailGrid>
  );
}

function AddressesDetail({
  addresses,
}: {
  readonly addresses: readonly GuestAddress[] | undefined;
}): React.JSX.Element {
  if (addresses === undefined || addresses.length === 0) {
    return <DetailField label="Addresses">No addresses on file</DetailField>;
  }

  return (
    <DetailGrid>
      {addresses.map((address) => (
        <DetailField
          key={address.id}
          label={`${address.type}${address.isPrimary ? ' (Primary)' : ''}`}
        >
          <div>
            <div>{address.line1}</div>
            {address.line2 !== null && address.line2 !== '' ? <div>{address.line2}</div> : null}
            <div>
              {[address.city, address.state, address.postalCode, address.countryCode]
                .filter((value) => value !== null && value !== '')
                .join(', ')}
            </div>
          </div>
        </DetailField>
      ))}
    </DetailGrid>
  );
}

export function GuestDetailPage(): React.JSX.Element {
  const guestId = useParams().guestId ?? '';
  const query = useGuest(guestId);
  const piiQuery = useGuestPii(guestId);

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.GUEST_READ}
      parent={{ to: '/guests', label: 'Guests' }}
      title={(_data) => piiQuery.data?.firstName ?? 'Guest'}
      actions={
        <PermissionGuard permission={PERMISSIONS.GUEST_WRITE}>
          <Button asChild variant="outline">
            <Link to={`/guests/${guestId}/edit`}>Edit</Link>
          </Button>
        </PermissionGuard>
      }
    >
      {(data) => <GuestDetailContent data={data} pii={piiQuery.data} piiQuery={piiQuery} />}
    </ResourceDetailPage>
  );
}
